/*
----------------------------------------------------------------------------
AI agent chat view
----------------------------------------------------------------------------
Usage:
import AiAgent from './modules/ai-agent.js'
_('ai-agent').nodes().forEach((el) => new AiAgent(el))

Softcat JS -> Softcat PHP (/api/ai-agent/*) -> private Chatbot API.
The browser never sees the Chatbot API URL or the upstream conversation id.
Responses stream as server-sent events over fetch(); if a proxy buffers the
stream the same parser still handles the whole body when it arrives.
*/

import { _ } from '../utils.js'
import { createLink, isSafeUrl, renderMarkdown } from './ai-markdown.js'
import { AI_AGENT_AVAILABILITY, AI_AGENT_OPEN, AI_AGENT_STATE } from './ai-prompt.js'

const SPRITE = '/application/themes/rawnet/app/images/sprite.svg'
const FOLLOW_THRESHOLD_PX = 120
const LATEST_GAP_PX = 24
const TOP_LINK_AFTER_PX = 400
const COMPOSER_MAX_HEIGHT = 200
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_PATTERN = /^\+?[0-9 ()-]{7,20}$/

const track = (event, params = {}) => {
    if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event, ...params })
    }
}

const el = (tag, className = '', text = null) => {
    const node = document.createElement(tag)
    if (className) {
        node.className = className
    }
    if (text !== null) {
        node.textContent = text
    }
    return node
}

const icon = (id, width, height = width) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', width)
    svg.setAttribute('height', height)
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use')
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `${SPRITE}#${id}`)
    svg.appendChild(use)
    return svg
}

const paragraphs = (text, className) => {
    const fragment = document.createDocumentFragment()
    String(text || '').split(/\n{2,}/).forEach((block) => {
        const p = el('p', className)
        block.split('\n').forEach((line, index) => {
            if (index > 0) {
                p.appendChild(document.createElement('br'))
            }
            p.appendChild(document.createTextNode(line))
        })
        fragment.appendChild(p)
    })
    return fragment
}

class SseParser {
    constructor() {
        this.buffer = ''
    }

    push(text) {
        this.buffer += text.replace(/\r\n?/g, '\n')
        const events = []
        let index
        while ((index = this.buffer.indexOf('\n\n')) !== -1) {
            const record = this.buffer.slice(0, index)
            this.buffer = this.buffer.slice(index + 2)
            const event = this.parse(record)
            if (event) {
                events.push(event)
            }
        }
        return events
    }

    flush() {
        const event = this.buffer.trim() ? this.parse(this.buffer) : null
        this.buffer = ''
        return event ? [event] : []
    }

    parse(record) {
        let name = 'message'
        const data = []
        record.split('\n').forEach((line) => {
            if (!line || line.startsWith(':')) {
                return
            }
            const separator = line.indexOf(':')
            const field = separator === -1 ? line : line.slice(0, separator)
            const value = separator === -1 ? '' : line.slice(separator + 1).replace(/^ /, '')
            if (field === 'event') {
                name = value
            } else if (field === 'data') {
                data.push(value)
            }
        })
        if (!data.length) {
            return null
        }
        try {
            return { event: name, data: JSON.parse(data.join('\n')) }
        } catch {
            return null
        }
    }
}

export default class AiAgent {
    constructor(root) {
        this.root = root
        this.endpoint = root.dataset.endpoint
        this.maxLength = parseInt(root.dataset.maxLength, 10) || 4000
        this.settings = JSON.parse(root.dataset.settings || '{}')

        this.log = _('ai-chat-log', 'element').nodeFrom(root)
        this.dock = _('ai-chat-dock', 'element').nodeFrom(root)
        this.composer = _('ai-chat-composer', 'element').nodeFrom(root)
        this.input = _('ai-chat-input', 'element').nodeFrom(root)
        this.sendButton = _('ai-chat-send', 'element').nodeFrom(root)
        this.limit = _('ai-chat-limit', 'element').nodeFrom(root)
        this.alert = _('ai-chat-alert', 'element').nodeFrom(root)
        this.offer = _('ai-chat-offer', 'element').nodeFrom(root)
        this.rating = _('ai-chat-rating', 'element').nodeFrom(root)
        this.header = document.querySelector('.header-wrap')
        this.topLink = _('ai-chat-top', 'element').nodeFrom(root)

        this.token = null
        this.available = true
        this.isOpen = false
        this.pushedHistory = false
        this.returnScroll = 0
        this.conversationReady = false
        this.busy = false
        this.controller = null
        this.contactAccepted = false
        this.ended = false
        this.follow = true
        this.lastScrollY = 0

        this.main = root.closest('main') || document.querySelector('main')
        if (this.main && root.parentElement !== this.main) {
            // A direct child of <main> can replace the landing page content without touching area markup.
            this.main.appendChild(root)
        }

        this.bindEvents()
        this.ready = this.fetchToken()

        if (window.location.hash === '#ai-chat') {
            history.replaceState(null, '', window.location.pathname + window.location.search)
        }
    }

    bindEvents = () => {
        document.addEventListener(AI_AGENT_OPEN, (event) => this.open((event.detail && event.detail.message) || ''))
        window.addEventListener('popstate', (event) => {
            if (this.isOpen && !(event.state && event.state.aiChat)) {
                this.hide(true)
            }
        })

        _('ai-chat-back', 'element').nodeFrom(this.root).addEventListener('click', () => this.hide(false))
        _('ai-chat-restart', 'element').nodeFrom(this.root).addEventListener('click', this.restart)
        _('ai-chat-offer-accept', 'element').nodeFrom(this.root).addEventListener('click', this.acceptContact)
        this.topLink.addEventListener('click', this.backToTop)
        this.bindRating()

        window.addEventListener('scroll', this.onScroll, { passive: true })
        if (this.header) {
            this.header.addEventListener('transitionend', this.updateStickyTop)
        }
        if ('ResizeObserver' in window) {
            new ResizeObserver(this.onDockResize).observe(this.dock)
        }

        this.composer.addEventListener('submit', (event) => {
            event.preventDefault()
            this.submitComposer()
        })
        this.input.addEventListener('input', this.onComposerInput)
        this.input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
                event.preventDefault()
                this.submitComposer()
            }
        })

        if (window.visualViewport) {
            const update = () => {
                const offset = Math.max(0, window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop)
                this.root.style.setProperty('--ai-keyboard-offset', `${offset}px`)
            }
            window.visualViewport.addEventListener('resize', update)
            window.visualViewport.addEventListener('scroll', update)
        }
    }

    // ------------------------------------------------------------------ API

    fetchToken = async () => {
        try {
            const response = await fetch(`${this.endpoint}/token`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            const data = await response.json()
            this.token = data.token
            this.available = Boolean(data.available)
        } catch {
            this.available = false
        }
        document.dispatchEvent(new CustomEvent(AI_AGENT_AVAILABILITY, { detail: { available: this.available } }))
    }

    post = async (path, body = {}, { signal, retryToken = true } = {}) => {
        const response = await fetch(`${this.endpoint}${path}`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream, application/json',
                'X-CSRF-Token': this.token || '',
            },
            body: JSON.stringify(body),
            signal,
        })
        if (response.status === 403 && retryToken) {
            await this.fetchToken()
            return this.post(path, body, { signal, retryToken: false })
        }
        return response
    }

    readJson = async (response) => {
        try {
            return await response.json()
        } catch {
            return {}
        }
    }

    createConversation = async () => {
        this.conversationReady = false
        try {
            const response = await this.post('/conversations')
            this.conversationReady = response.ok
        } catch {
            this.conversationReady = false
        }
        return this.conversationReady
    }

    // ------------------------------------------------------------ view state

    open = async (message = '') => {
        await this.ready
        if (!this.isOpen) {
            this.show()
        }
        if (!this.available) {
            this.showUnavailable()
            return
        }
        if (this.ended) {
            // A finished conversation stays readable until the visitor asks something new.
            this.resetConversation()
        }
        if (!this.conversationReady && !(await this.createConversation())) {
            this.showUnavailable()
            return
        }
        if (message && !this.ended) {
            this.send(message)
        } else {
            this.focusComposer()
        }
    }

    show = () => {
        this.isOpen = true
        this.returnScroll = window.scrollY
        this.root.hidden = false
        this.setBackgroundInert(true)
        document.body.classList.add('ai-chat-is-open')
        history.pushState({ aiChat: true }, '', '#ai-chat')
        this.pushedHistory = true
        window.scrollTo(0, 0)
        this.lastScrollY = 0
        this.follow = true
        this.updateStickyTop()
        document.dispatchEvent(new CustomEvent(AI_AGENT_STATE, { detail: { open: true } }))
        track('ai_agent_open')
    }

    hide = (fromHistory) => {
        if (!this.isOpen) {
            return
        }
        this.isOpen = false
        this.root.hidden = true
        this.setBackgroundInert(false)
        document.body.classList.remove('ai-chat-is-open')
        document.dispatchEvent(new CustomEvent(AI_AGENT_STATE, { detail: { open: false } }))
        if (!fromHistory && this.pushedHistory) {
            this.pushedHistory = false
            history.back()
        }
        this.pushedHistory = false
        window.requestAnimationFrame(() => window.scrollTo(0, this.returnScroll))
    }

    setBackgroundInert = (inert) => {
        if (!this.main) {
            return
        }
        this.main.classList.toggle('ai-chat-open', inert)
        Array.from(this.main.children).forEach((child) => {
            if (child !== this.root) {
                child.inert = inert
            }
        })
    }

    confirmRestart = () => new Promise((resolve) => {
        const dialog = _('ai-chat-confirm', 'element').nodeFrom(this.root)
        if (!dialog || typeof dialog.showModal !== 'function') {
            resolve(true)
            return
        }
        dialog.returnValue = ''
        dialog.addEventListener('close', () => resolve(dialog.returnValue === 'confirm'), { once: true })
        dialog.showModal()
    })

    resetConversation = () => {
        if (this.controller) {
            this.controller.abort()
            this.controller = null
        }
        this.log.replaceChildren()
        this.contactAccepted = false
        this.ended = false
        this.conversationReady = false
        this.offer.hidden = true
        this.dock.hidden = false
        this.setBusy(false)
    }

    restart = async () => {
        if (!(await this.confirmRestart())) {
            return
        }
        this.resetConversation()
        track('ai_agent_restart')
        if (await this.createConversation()) {
            this.focusComposer()
        } else {
            this.showUnavailable()
        }
    }

    showUnavailable = () => {
        this.log.replaceChildren()
        const message = this.agentShell()
        message.body.appendChild(paragraphs(this.settings.unavailableMessage, 'ai-message__text'))
        const actions = el('div', 'ai-message__actions')
        const back = el('button', 'button button--inverted', this.settings.closingButtonLabel)
        back.type = 'button'
        back.addEventListener('click', () => this.hide(false))
        actions.appendChild(back)
        if (this.settings.alternativeContactUrl && isSafeUrl(this.settings.alternativeContactUrl)) {
            actions.appendChild(createLink(this.settings.alternativeContactLabel, this.settings.alternativeContactUrl, 'button'))
        }
        message.body.appendChild(actions)
        message.root.removeAttribute('aria-busy')
        this.log.appendChild(message.root)
        this.dock.hidden = true
        this.announce(this.settings.unavailableMessage)
        back.focus()
    }

    // --------------------------------------------------------------- composer

    onComposerInput = () => {
        this.input.style.height = 'auto'
        this.input.style.height = `${Math.min(this.input.scrollHeight, COMPOSER_MAX_HEIGHT)}px`
        this.limit.hidden = this.input.value.length < this.maxLength
        this.updateSendState()
    }

    updateSendState = () => {
        this.sendButton.disabled = this.busy || this.ended || this.input.value.trim() === ''
    }

    setBusy = (busy) => {
        this.busy = busy
        this.updateSendState()
    }

    submitComposer = () => {
        const message = this.input.value.trim()
        if (!message || this.busy || this.ended) {
            return
        }
        this.input.value = ''
        this.onComposerInput()
        this.send(message)
    }

    focusComposer = () => {
        if (!this.dock.hidden) {
            this.input.focus({ preventScroll: true })
        }
    }

    // --------------------------------------------------------------- messages

    // Distance the end of the conversation sits below the visible area above the fixed composer dock.
    distanceToLatest = () => {
        const visibleBottom = this.dock.hidden ? window.innerHeight : this.dock.getBoundingClientRect().top
        return this.log.getBoundingClientRect().bottom + LATEST_GAP_PX - visibleBottom
    }

    keepLatestInView = (force = false) => {
        if (force) {
            this.follow = true
        }
        if (!this.follow) {
            return
        }
        const distance = this.distanceToLatest()
        if (distance > 0) {
            window.scrollBy({ top: distance, behavior: 'instant' })
        }
    }

    onScroll = () => {
        if (!this.isOpen) {
            return
        }
        this.updateStickyTop()
        const y = window.scrollY
        // Follow scrolls only ever move down, so any upward movement is the visitor re-reading: pause until they return.
        if (y < this.lastScrollY - 1) {
            this.follow = false
        } else if (this.distanceToLatest() <= FOLLOW_THRESHOLD_PX) {
            this.follow = true
        }
        this.lastScrollY = y
        this.topLink.hidden = y < TOP_LINK_AFTER_PX
    }

    backToTop = () => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' })
        _('ai-chat-back', 'element').nodeFrom(this.root).focus({ preventScroll: true })
    }

    onDockResize = () => {
        this.root.style.setProperty('--ai-dock-height', `${this.dock.offsetHeight}px`)
        if (this.isOpen) {
            this.keepLatestInView()
        }
    }

    updateStickyTop = () => {
        const top = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0
        this.root.style.setProperty('--ai-sticky-top', `${Math.round(top)}px`)
    }

    appendVisitor = (text) => {
        const wrapper = el('div', 'ai-message ai-message--visitor')
        wrapper.appendChild(el('p', 'ai-message__bubble', text))
        this.log.appendChild(wrapper)
        this.keepLatestInView(true)
    }

    agentShell = () => {
        const root = el('div', 'ai-message ai-message--agent')
        root.setAttribute('aria-busy', 'true')
        const body = el('div', 'ai-message__body')
        root.append(body)
        return { root, body }
    }

    appendAgentText = (text) => {
        const message = this.agentShell()
        message.body.appendChild(paragraphs(text, 'ai-message__text'))
        message.root.removeAttribute('aria-busy')
        this.log.appendChild(message.root)
        this.keepLatestInView()
        return message
    }

    createAgentMessage = () => {
        const shell = this.agentShell()
        const content = el('div', 'ai-message__content')
        const progress = el('p', 'ai-message__progress')
        const dots = el('span', 'ai-typing')
        dots.setAttribute('aria-hidden', 'true')
        dots.append(el('i'), el('i'), el('i'))
        const activity = el('span', 'ai-message__activity')
        progress.append(dots, el('span', 'sr-only', 'The AI agent is responding'), activity)
        shell.body.append(content, progress)
        this.log.appendChild(shell.root)
        this.keepLatestInView(true)

        return {
            ...shell,
            content,
            progress,
            activity,
            text: '',
            messageId: null,
            contactReady: false,
            terminated: false,
            frame: null,
        }
    }

    renderMessage = (message) => {
        message.frame = null
        message.content.replaceChildren(renderMarkdown(message.text))
        this.keepLatestInView()
    }

    scheduleRender = (message) => {
        if (!message.frame) {
            message.frame = window.requestAnimationFrame(() => this.renderMessage(message))
        }
    }

    send = async (text, isRetry = false) => {
        if (this.busy || this.ended) {
            return
        }
        if (text.length > this.maxLength) {
            this.announce(this.limit.textContent)
            return
        }
        if (!isRetry) {
            this.appendVisitor(text)
        }

        const message = this.createAgentMessage()
        const controller = new AbortController()
        this.controller = controller
        this.setBusy(true)
        track('ai_agent_message_sent')

        try {
            let response = await this.post('/messages', { message: text }, { signal: controller.signal })
            if (response.status === 409) {
                const data = await this.readJson(response.clone())
                if (data.error && data.error.code === 'no_conversation' && (await this.createConversation())) {
                    response = await this.post('/messages', { message: text }, { signal: controller.signal })
                }
            }

            const type = response.headers.get('Content-Type') || ''
            if (!response.ok || !type.includes('text/event-stream')) {
                const data = await this.readJson(response)
                this.failMessage(message, (data.error && data.error.message) || this.settings.errorMessage, () => this.retry(message, text))
                return
            }

            await this.readStream(response, message, text)
        } catch (error) {
            if (error.name === 'AbortError') {
                message.root.remove()
                return
            }
            this.failMessage(message, this.settings.errorMessage, () => this.retry(message, text))
        } finally {
            if (this.controller === controller) {
                this.controller = null
                this.setBusy(false)
            }
        }
    }

    retry = (message, text) => {
        message.root.remove()
        this.send(text, true)
    }

    readStream = async (response, message, text) => {
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        const parser = new SseParser()

        let done = false
        while (!done) {
            const chunk = await reader.read()
            done = chunk.done
            const events = done ? parser.push(decoder.decode()).concat(parser.flush()) : parser.push(decoder.decode(chunk.value, { stream: true }))
            events.forEach((event) => this.handleEvent(message, event, text))
        }

        if (!message.terminated) {
            this.failMessage(message, this.settings.errorMessage, () => this.retry(message, text))
        }
    }

    handleEvent = (message, { event, data }, text) => {
        switch (event) {
            case 'message.started':
                message.messageId = data.messageId
                break
            case 'tool.started':
                message.activity.textContent = data.displayName ? `${data.displayName}…` : ''
                break
            case 'tool.completed':
                message.activity.textContent = ''
                break
            case 'message.delta':
                message.text += data.delta || ''
                this.scheduleRender(message)
                break
            case 'contact.ready':
                message.contactReady = true
                break
            case 'message.completed':
                message.terminated = true
                message.text = data.outputMessage || message.text
                this.completeMessage(message)
                break
            case 'message.failed':
                message.terminated = true
                this.failMessage(message, data.message || this.settings.errorMessage, data.retryable === false ? null : () => this.retry(message, text))
                break
            default:
                break
        }
    }

    completeMessage = (message) => {
        if (message.frame) {
            window.cancelAnimationFrame(message.frame)
        }
        message.content.replaceChildren(renderMarkdown(message.text))
        message.progress.remove()
        if (message.messageId) {
            message.body.appendChild(this.feedback(message.messageId))
        }
        if (message.contactReady && !this.contactAccepted && this.offer.hidden) {
            message.body.appendChild(this.contactOffer())
            track('ai_agent_contact_ready')
        }
        message.root.removeAttribute('aria-busy')
        this.keepLatestInView()
    }

    failMessage = (message, text, retry) => {
        message.terminated = true
        message.progress.remove()
        const error = el('div', 'ai-message__error')
        error.setAttribute('role', 'alert')
        error.appendChild(el('p', '', text))
        if (retry) {
            const button = el('button', 'button', 'Try again')
            button.type = 'button'
            button.addEventListener('click', retry, { once: true })
            error.appendChild(button)
        }
        message.body.appendChild(error)
        message.root.removeAttribute('aria-busy')
        this.keepLatestInView()
        track('ai_agent_message_failed')
    }

    feedback = (messageId) => {
        const group = el('div', 'ai-feedback')
        group.setAttribute('role', 'group')
        group.setAttribute('aria-label', 'Was this response helpful?')
        const status = el('span', 'sr-only')
        status.setAttribute('aria-live', 'polite')

        const buttons = [['positive', 'Helpful'], ['negative', 'Not helpful']].map(([rating, label]) => {
            const button = el('button', `ai-feedback__button ai-feedback__button--${rating}`)
            button.type = 'button'
            button.setAttribute('aria-pressed', 'false')
            button.append(icon('like', 20), el('span', 'sr-only', label))
            button.addEventListener('click', async () => {
                buttons.forEach((b) => { b.disabled = true })
                try {
                    const response = await this.post('/feedback', { messageId, rating })
                    if (!response.ok) {
                        throw new Error('feedback')
                    }
                    button.setAttribute('aria-pressed', 'true')
                    status.textContent = 'Thanks for your feedback.'
                    track('ai_agent_feedback', { rating })
                } catch {
                    buttons.forEach((b) => { b.disabled = false })
                    status.textContent = 'Sorry, your feedback could not be sent.'
                }
            })
            return button
        })

        group.append(...buttons, status)
        return group
    }

    contactOffer = () => {
        const offer = el('div', 'ai-contact-offer')
        const accept = el('button', 'button button--inverted', this.settings.acceptLabel)
        const decline = el('button', 'button', this.settings.declineLabel)
        accept.type = 'button'
        decline.type = 'button'

        accept.addEventListener('click', () => {
            accept.disabled = true
            decline.disabled = true
            offer.classList.add('is-inactive')
            this.acceptContact()
        })
        decline.addEventListener('click', () => {
            // The offer moves above the composer so it stays one click away while the visitor keeps learning.
            offer.remove()
            this.offer.hidden = false
            track('ai_agent_contact_decline')
            this.send(this.settings.declineLabel)
            this.focusComposer()
        })

        offer.append(accept, decline)
        return offer
    }

    acceptContact = () => {
        this.contactAccepted = true
        this.offer.hidden = true
        track('ai_agent_contact_accept')
        this.appendAgentText(this.settings.acceptConfirmation)
        const form = this.leadForm()
        this.log.appendChild(form)
        form.querySelector('input').focus()
    }

    // ---------------------------------------------------------- chat feedback

    bindRating = () => {
        if (!this.rating) {
            return
        }
        this.ratingForm = _('ai-chat-rating-form', 'element').nodeFrom(this.rating)
        this.ratingError = _('ai-chat-rating-error', 'element').nodeFrom(this.rating)
        this.ratingSubmit = _('ai-chat-rating-submit', 'element').nodeFrom(this.rating)
        this.ratingThanks = _('ai-chat-rating-thanks', 'element').nodeFrom(this.rating)
        const opener = _('ai-chat-rating-open', 'element').nodeFrom(this.root)

        opener.addEventListener('click', () => this.openRating(opener))
        _('ai-chat-rating-close', 'element').nodesFrom(this.rating).forEach((button) => {
            button.addEventListener('click', () => this.rating.close())
        })
        this.ratingForm.addEventListener('change', () => this.setRatingError(''))
        this.ratingForm.addEventListener('submit', (event) => {
            event.preventDefault()
            this.submitRating()
        })
    }

    openRating = (opener) => {
        this.ratingForm.hidden = false
        this.ratingThanks.hidden = true
        this.setRatingError('')
        this.rating.addEventListener('close', () => opener.focus({ preventScroll: true }), { once: true })
        this.rating.showModal()
        const star = this.ratingForm.querySelector('input[name="rating"]:checked') || this.ratingForm.querySelector('input[name="rating"]')
        star.focus()
        track('ai_agent_rating_open')
    }

    setRatingError = (message) => {
        this.ratingError.textContent = message
        this.ratingError.hidden = !message
    }

    submitRating = async () => {
        const { elements } = this.ratingForm
        const rating = Number(elements.rating.value)
        if (!rating) {
            this.setRatingError('Choose a rating from 1 to 5 stars.')
            this.ratingForm.querySelector('input[name="rating"]').focus()
            return
        }

        this.setRatingError('')
        this.ratingSubmit.disabled = true
        try {
            const response = await this.post('/chat-feedback', {
                rating,
                message: elements.message.value.trim(),
                website: elements.website.value,
            })
            const data = await this.readJson(response)
            if (!response.ok) {
                throw new Error((data.errors && Object.values(data.errors)[0]) || (data.error && data.error.message) || this.settings.errorMessage)
            }
            this.ratingForm.reset()
            this.ratingForm.hidden = true
            this.ratingThanks.hidden = false
            this.ratingThanks.querySelector('button').focus()
            track('ai_agent_rating', { rating })
        } catch (error) {
            this.setRatingError(error.message || this.settings.errorMessage)
        } finally {
            this.ratingSubmit.disabled = false
        }
    }

    // -------------------------------------------------------------- lead form

    leadFields = () => [
        { name: 'firstName', label: 'First name', type: 'text', autocomplete: 'given-name', required: true, message: 'Enter your first name.' },
        { name: 'lastName', label: 'Last name', type: 'text', autocomplete: 'family-name', required: true, message: 'Enter your last name.' },
        { name: 'email', label: 'Email address', type: 'email', autocomplete: 'email', inputmode: 'email', required: true, message: 'Enter an email address in the correct format, like name@example.com.', pattern: EMAIL_PATTERN },
        { name: 'phone', label: 'Contact number', type: 'tel', autocomplete: 'tel', inputmode: 'tel', required: true, message: 'Enter a contact number, like 01628 403 403.', pattern: PHONE_PATTERN },
        { name: 'company', label: 'Business name', type: 'text', autocomplete: 'organization', required: true, message: 'Enter your business name.' },
        { name: 'organisationSize', label: 'Number of employees', type: 'select', required: false },
    ]

    leadForm = () => {
        const id = `ai-lead-${Date.now()}`
        const form = el('form', 'ai-lead')
        form.noValidate = true
        form.setAttribute('aria-label', 'Speak to a Softcat expert')

        const summary = el('p', 'ai-lead__summary')
        summary.setAttribute('role', 'alert')
        summary.hidden = true
        form.appendChild(summary)

        const fields = this.leadFields()
        fields.forEach((field) => {
            const group = el('div', 'ai-lead__field')
            const label = el('label', 'ai-lead__label', field.required ? `${field.label}*` : field.label)
            label.htmlFor = `${id}-${field.name}`
            let control
            if (field.type === 'select') {
                control = el('select', 'ai-lead__input ai-lead__input--select')
                const placeholder = el('option', '', 'Please select...')
                placeholder.value = ''
                control.appendChild(placeholder)
                const sizes = this.settings.organisationSizes || []
                sizes.forEach((size) => {
                    const option = el('option', '', size)
                    option.value = size
                    control.appendChild(option)
                })
            } else {
                control = el('input', 'ai-lead__input')
                control.type = field.type
                control.autocomplete = field.autocomplete
                if (field.inputmode) {
                    control.inputMode = field.inputmode
                }
            }
            control.id = `${id}-${field.name}`
            control.name = field.name
            if (field.required) {
                control.required = true
                control.setAttribute('aria-required', 'true')
            }
            const error = el('p', 'ai-lead__error')
            error.id = `${id}-${field.name}-error`
            error.hidden = true
            control.addEventListener('input', () => {
                if (control.getAttribute('aria-invalid') === 'true' && !this.fieldError(field, control)) {
                    this.setFieldError(control, error, '')
                }
            })
            group.append(label, control, error)
            form.appendChild(group)
        })

        const trap = el('div', 'ai-lead__trap')
        trap.setAttribute('aria-hidden', 'true')
        const trapInput = el('input')
        trapInput.name = 'website'
        trapInput.tabIndex = -1
        trapInput.autocomplete = 'off'
        trap.appendChild(trapInput)
        form.appendChild(trap)

        const consentGroup = el('div', 'ai-lead__consent')
        const consent = el('input', 'ai-lead__checkbox')
        consent.type = 'checkbox'
        consent.id = `${id}-consent`
        consent.name = 'consent'
        consent.required = true
        consent.setAttribute('aria-required', 'true')
        const consentLabel = el('label', 'ai-lead__consent-label')
        consentLabel.htmlFor = consent.id
        const [before, after] = String(this.settings.consentText || '').split('{privacy}')
        consentLabel.appendChild(document.createTextNode(before || ''))
        if (after !== undefined && isSafeUrl(this.settings.privacyUrl)) {
            consentLabel.appendChild(createLink(this.settings.privacyLabel, this.settings.privacyUrl))
            consentLabel.appendChild(document.createTextNode(after))
        }
        const consentError = el('p', 'ai-lead__error')
        consentError.id = `${consent.id}-error`
        consentError.hidden = true
        consent.addEventListener('change', () => {
            if (consent.checked) {
                this.setFieldError(consent, consentError, '')
            }
        })
        consentGroup.append(consent, consentLabel, consentError)
        form.appendChild(consentGroup)

        const submit = el('button', 'button button--inverted ai-lead__submit', this.settings.formSubmitLabel)
        submit.type = 'submit'
        form.appendChild(submit)

        form.addEventListener('submit', (event) => {
            event.preventDefault()
            this.submitLead(form, fields, { consent, consentError, submit, summary, trapInput })
        })

        return form
    }

    fieldError = (field, control) => {
        const value = control.value.trim()
        if (field.required && !value) {
            return field.message
        }
        if (value && field.pattern && !field.pattern.test(value)) {
            return field.message
        }
        return ''
    }

    setFieldError = (control, error, message) => {
        error.textContent = message
        error.hidden = !message
        if (message) {
            control.setAttribute('aria-invalid', 'true')
            control.setAttribute('aria-describedby', error.id)
        } else {
            control.removeAttribute('aria-invalid')
            control.removeAttribute('aria-describedby')
        }
    }

    submitLead = async (form, fields, refs) => {
        const { consent, consentError, submit, summary, trapInput } = refs
        const invalid = []

        fields.forEach((field) => {
            const control = form.elements[field.name]
            const error = form.querySelector(`#${CSS.escape(control.id)}-error`)
            const message = this.fieldError(field, control)
            this.setFieldError(control, error, message)
            if (message) {
                invalid.push(control)
            }
        })
        const consentMessage = consent.checked ? '' : 'Tick the box to confirm you are happy to be contacted.'
        this.setFieldError(consent, consentError, consentMessage)
        if (consentMessage) {
            invalid.push(consent)
        }

        if (invalid.length) {
            summary.textContent = `There ${invalid.length === 1 ? 'is a problem' : `are ${invalid.length} problems`} with the form. Please check the highlighted fields.`
            summary.hidden = false
            invalid[0].focus()
            return
        }

        summary.hidden = true
        submit.disabled = true
        const payload = { consent: true, website: trapInput.value }
        fields.forEach((field) => {
            payload[field.name] = form.elements[field.name].value.trim()
        })

        try {
            const response = await this.post('/leads', payload)
            const data = await this.readJson(response)

            if (response.status === 422 && data.errors) {
                submit.disabled = false
                const controls = Object.keys(data.errors).map((name) => {
                    const control = name === 'consent' ? consent : form.elements[name]
                    if (control) {
                        const error = name === 'consent' ? consentError : form.querySelector(`#${CSS.escape(control.id)}-error`)
                        this.setFieldError(control, error, data.errors[name])
                    }
                    return control
                }).filter(Boolean)
                summary.textContent = 'Please check the highlighted fields.'
                summary.hidden = false
                if (controls[0]) {
                    controls[0].focus()
                }
                return
            }
            if (!response.ok) {
                throw new Error((data.error && data.error.message) || this.settings.errorMessage)
            }

            this.leadSent(form, submit)
        } catch (error) {
            submit.disabled = false
            summary.replaceChildren(document.createTextNode(`${error.message || this.settings.errorMessage} `))
            if (this.settings.alternativeContactUrl && isSafeUrl(this.settings.alternativeContactUrl)) {
                summary.appendChild(createLink(this.settings.alternativeContactLabel, this.settings.alternativeContactUrl))
            }
            summary.hidden = false
        }
    }

    leadSent = (form, submit) => {
        Array.from(form.elements).forEach((control) => {
            control.disabled = true
        })
        submit.textContent = this.settings.formSentLabel
        submit.classList.add('is-sent')
        track('ai_agent_lead_submitted')

        const closing = this.appendAgentText(this.settings.closingMessage)
        const back = el('button', 'button button--inverted', this.settings.closingButtonLabel)
        back.type = 'button'
        back.addEventListener('click', () => this.hide(false))
        const actions = el('div', 'ai-message__actions')
        actions.appendChild(back)
        closing.body.appendChild(actions)

        this.ended = true
        this.dock.hidden = true
        this.updateSendState()
        back.focus()
    }

    announce = (text) => {
        this.alert.textContent = ''
        window.setTimeout(() => {
            this.alert.textContent = text
        }, 50)
    }
}
